<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">{{ $t("MAIL_V2.MAIL_LOGS") }}</h3>
          </div>
        </div>
      </div>

      <div class="page-wrapper-table">
        <div class="page-wrapper-table-header">
          <el-select
            class="select-primary pagination-select"
            v-model="pagination.perPage"
            :placeholder="$t('COMMON.PER_PAGE')"
          >
            <el-option v-for="item in pagination.perPageOptions" :key="item" :label="item" :value="item" />
          </el-select>

          <organization-selector
            v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
            @organizationChanged="(id) => { selectedOrganization = id; }"
          />

          <el-select v-model="filterStatus" :placeholder="$t('MAIL_V2.ALL_STATUS')" clearable>
            <el-option value="sent" :label="$t('MAIL_V2.STATUS_SENT')" />
            <el-option value="failed" :label="$t('MAIL_V2.STATUS_FAILED')" />
            <el-option value="pending" :label="$t('MAIL_V2.STATUS_PENDING')" />
            <el-option value="opened" :label="$t('MAIL_V2.STATUS_OPENED')" />
            <el-option value="clicked" :label="$t('MAIL_V2.STATUS_CLICKED')" />
          </el-select>

          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH')"
            clearable
          />
        </div>

        <div class="page-wrapper-table-body">
          <div class="page-wrapper-table-body-inner">
            <el-table
              class="align-items-center table-flush"
              header-row-class-name="thead-light"
              :data="logs"
              @sort-change="sortChange"
            >
              <div class="table-loading" slot="empty" v-if="loading">
                <img src="/img/loading.gif" />
              </div>

              <el-table-column :label="$t('MAIL_V2.RECIPIENT')" prop="recipient_email" sortable="custom" min-width="220">
                <template v-slot="{ row }">
                  <div>
                    <strong>{{ row.recipient_email }}</strong>
                    <br />
                    <span class="text-muted" style="font-size:11px;">{{ row.from_name }} &lt;{{ row.from_email }}&gt;</span>
                  </div>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.SUBJECT')" prop="subject_rendered" min-width="250">
                <template v-slot="{ row }">
                  <span>{{ row.subject_rendered }}</span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.TYPE')" min-width="160">
                <template v-slot="{ row }">
                  <div v-if="row.mail_type">
                    <strong>{{ row.mail_type.name?.fr || row.mail_type.name?.en || "" }}</strong>
                    <br />
                    <span class="text-muted" style="font-size:11px;font-family:monospace;">{{ row.mail_type.code }}</span>
                  </div>
                  <span v-else class="text-muted">—</span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.STATUS')" min-width="110">
                <template v-slot="{ row }">
                  <span class="log-status" :class="'log-status--' + row.status">
                    {{ $t("MAIL_V2.STATUS_" + row.status.toUpperCase()) }}
                  </span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.LOCALE')" min-width="70">
                <template v-slot="{ row }">
                  <span class="locale-chip" v-if="row.locale">{{ row.locale.toUpperCase() }}</span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('COMMON.CREATED_AT')" prop="created_at" sortable="custom" min-width="160">
                <template v-slot="{ row }">
                  {{ $formatDate(row.created_at) || (row.created_at | moment("DD/MM/YYYY HH:mm")) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <div class="page-wrapper-table-footer">
          <p class="card-category">
            {{
              $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
                from: total ? from + 1 : 0,
                to,
                of: total,
              })
            }}
          </p>
          <base-pagination
            class="pagination-no-border"
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import { Table, TableColumn, Select, Option } from "element-ui";
import { BasePagination } from "@/components";
import { mapGetters } from "vuex";
import OrganizationSelector from "@/components/OrganizationSelector.vue";

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    BasePagination,
    OrganizationSelector,
  },

  data() {
    return {
      query: "",
      selectedOrganization: null,
      filterStatus: null,
      sort: "-created_at",
      loading: false,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
    };
  },

  computed: {
    ...mapGetters("organizations", { globalOrganization: "globalOrganization" }),
    ...mapGetters("profile", { me: "me" }),
    ...mapGetters("mailV2", { logs: "logs", logsMeta: "logsMeta" }),

    organizationId() {
      return this.selectedOrganization || this.globalOrganization?.id || this.me?.organization?.id;
    },
    total() { return this.logsMeta?.total || 0; },
    from() { return (this.pagination.currentPage - 1) * this.pagination.perPage; },
    to() { return Math.min(this.from + this.pagination.perPage, this.total); },
  },

  watch: {
    organizationId: { handler(v) { if (v) this.getList(); }, immediate: true },
    "pagination.currentPage"() { this.getList(); },
    "pagination.perPage"() { this.pagination.currentPage = 1; this.getList(); },
    selectedOrganization() { this.pagination.currentPage = 1; this.getList(); },
    filterStatus() { this.pagination.currentPage = 1; this.getList(); },
    query: _.debounce(function () { this.pagination.currentPage = 1; this.getList(); }, 400),
  },

  methods: {
    async getList() {
      if (!this.organizationId) return;
      this.loading = true;
      try {
        await this.$store.dispatch("mailV2/listLogs", {
          orgId: this.organizationId,
          params: {
            sort: this.sort,
            page: {
              number: this.pagination.currentPage,
              size: this.pagination.perPage,
            },
            filter: {
              ...(this.filterStatus ? { status: this.filterStatus } : {}),
              ...(this.query ? { recipient_email: this.query } : {}),
            },
          },
        });
      } finally {
        this.loading = false;
      }
    },

    sortChange({ prop, order }) {
      if (prop && order) {
        this.sort = order === "descending" ? `-${prop}` : prop;
      } else {
        this.sort = "-created_at";
      }
      this.getList();
    },
  },
};
</script>

<style scoped>
.log-status {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.log-status--sent { background: #e8f5e9; color: #2e7d32; }
.log-status--failed { background: #ffebee; color: #c62828; }
.log-status--pending { background: #fff3e0; color: #e65100; }
.log-status--opened { background: #e3f2fd; color: #1565c0; }
.log-status--clicked { background: #f3e5f5; color: #7b1fa2; }
.log-status--bounced { background: #fce4ec; color: #ad1457; }

.locale-chip {
  display: inline-block;
  padding: 2px 6px;
  background: #f0f0f0;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  color: #666;
}
</style>
